import { useCallback, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { OrganizationsAPI } from '@makyoapp/makyo-sdk'
import { OrgMemberRole } from '@makyoapp/makyo-sdk/models/ACL'
import { MemberAdapter, OrgCardAdapter } from '@makyoapp/makyo-common'
import { useFormEnter } from '@/shared/makyo-hooks'
import { ArrowRight16 } from '@makyoapp/makyo-icon/icon'
import {
  AvatarCell,
  AvatarSize,
  DeleteModal,
  Field,
  ImgEasyResult,
  Label,
  ModalGrid,
  SelectDropdown,
} from '@makyoapp/makyo-ui'

import { lazyModal } from '@/lib/lazy-load'
import { toasted } from '@/lib/toast'
import { useHandleLeaveOrg, useOrgRemove, useOrgTransferOwnership } from '@/resolvers/org/api'
import { useCacheContext } from '@/context/CacheContext'
import useAuth from '@/hooks/useAuth'
import { ORG_MEMBER_ROUTE, ORG_ROUTE } from '@/shared/constants'

import { useOrgsContext } from '../context/OrgsContext'
import { useReqError } from '../hooks/useReqError'
import { Info } from './Info'
import { OrgCard } from './OrgCard'

const BasicInfoEditModal = lazyModal(() => import('./BasicInfoEditModal'))

function filterApprovedNotOwnerMember(members: MemberAdapter[]) {
  return members.filter((m) => {
    return m.orgRole !== OrgMemberRole.Owner && m.status !== 'pending'
  })
}

interface OrgDeleteModalProps {
  id?: string
  /**
   * should show modal or not
   */
  show: boolean
  /**
   * called when modal is closed
   *
   * is it hide after form submission or hide when close button clicked
   * @param isSubmit
   */
  onHide: (isSubmit: boolean) => void
  /**
   * the org to be deleted
   */
  org: OrgCardAdapter
  /**
   * member to transfer the org and it's asset to
   */
  transferTo?: MemberAdapter
  /**
   * set transfer to
   */
  setTransferTo: (newTo?: MemberAdapter) => void
  /**
   * called to handle delete submission
   *
   * target member to transfer the org if delete is unavailable
   * @param transferTarget
   */
  onSubmit: (done?: () => void) => void
}

/**
 * delete org modal
 */
export function OrgDeleteModal(props: OrgDeleteModalProps) {
  const { t } = useTranslation()
  const { id, show, org, transferTo, setTransferTo, onSubmit, onHide } = props

  /**
   * filter only approved member to show in transfer list
   */
  const filteredMembers = filterApprovedNotOwnerMember(org.members)
  const hasOthers = filteredMembers.length > 0 && (org.appCount || 0) > 0
  const [step, setStep] = useState(1)
  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  const handleHide = useCallback(
    (isSubmit: boolean | undefined = false) => {
      setStep(1)
      setIsBtnLoading(false)
      onHide(isSubmit)
    },
    [onHide, setStep, setIsBtnLoading]
  )

  const handleSubmit = useCallback(() => {
    setIsBtnLoading(true)
    onSubmit(() => handleHide(true))
  }, [onSubmit, setIsBtnLoading, handleHide])

  if (hasOthers && step === 1) {
    return (
      <DeleteModal
        id={id}
        show={show}
        onHide={() => handleHide()}
        onSubmit={handleSubmit}
        title={t('Remove Organization')}
        continueLabel={t('No, delete org')}
        withCancel
        cancelLabel={t('Yes, select member')}
        onCancel={() => setStep(2)}
      >
        <div className='modal-md max-w-full'>
          <div className='text-base'>
            {t(
              'This organization has at least one app. Would you like to transfer the organization to another member?'
            )}
          </div>
        </div>
      </DeleteModal>
    )
  }

  if (step === 2 && hasOthers) {
    return (
      <OrgTransferModal
        setTransferTo={setTransferTo}
        transferTo={transferTo}
        onSubmit={handleSubmit}
        show={show}
        onHide={handleHide}
        org={org}
      />
    )
  }

  return (
    <DeleteModal
      id={id}
      show={show}
      isLoading={isBtnLoading}
      onHide={handleHide}
      onSubmit={handleSubmit}
      title={t('Remove Organization')}
      continueLabel={t('Yes, remove')}
      withCancel
    >
      <div className='modal-md max-w-full'>
        <div className='text-sm mb-2'>{t("Are you sure you'd like to remove this organization?")}</div>
        <div className='text-light'>
          {t(
            "This organization's teams, apps, and databases will also be removed.  Removed assets can be found in the Trash and restored within 14 days."
          )}
        </div>
      </div>
    </DeleteModal>
  )
}

interface OrgTransferModalProps {
  id?: string
  /**
   * should show modal or not
   */
  show: boolean
  /**
   * called when modal is closed
   */
  onHide: () => void
  /**
   * the org to be transfer
   */
  org: OrgCardAdapter
  /**
   * member to transfer the org and it's asset to
   */
  transferTo?: MemberAdapter
  /**
   * set transfer to
   */
  setTransferTo: (newTo?: MemberAdapter) => void
  /**
   * on submit handle
   * @param done
   */
  onSubmit: (done?: () => void) => void
  /**
   * show delete notice or not
   */
  withDelete?: boolean
}

/**
 * org transfer modal
 */
export function OrgTransferModal(props: OrgTransferModalProps) {
  const { userData } = useAuth()
  const { t } = useTranslation()
  const { show, onHide, org, transferTo, setTransferTo, onSubmit, withDelete } = props

  const [isBtnLoading, setIsBtnLoading] = useBoolean(false)

  const formId = 'transfer-org'

  const [step, setStep] = useState(1)

  const handleHide = () => {
    setStep(1)
    setIsBtnLoading(false)
    onHide()
  }

  const handleSubmit = () => {
    if (step === 1) {
      setStep(2)
      return
    }

    if (transferTo) {
      setIsBtnLoading(true)
      onSubmit(handleHide)
    }
  }

  useFormEnter(formId, () => {
    handleSubmit()
  })

  const filteredMembers = filterApprovedNotOwnerMember(org.members).filter((member) => member.userId !== userData?.id)

  const showDeleteNotice = withDelete && filteredMembers.length > 1

  return (
    <ModalGrid
      id={props.id}
      show={show}
      onHide={handleHide}
      submitId={props.id ? `${props.id}-${step === 1 ? 'trf-btn' : 'trf-submit'}` : undefined}
      leadingTitle={t('Cancel')}
      primaryTrailing={step === 1 ? t('Transfer') : t('Yes, transfer')}
      isLoading={isBtnLoading}
      onCancel={handleHide}
      onConfirm={handleSubmit}
      title={t('Transfer Ownership')}
      cancelId={props.id ? `${props.id}-cancel-btn` : undefined}
      disabled={!transferTo || isBtnLoading}
    >
      <form
        id={formId}
        onSubmit={(e) => {
          e.preventDefault()
          handleSubmit()
        }}
      >
        {showDeleteNotice && (
          <Info>
            <Trans i18nKey='deleteNoticeWithOtherNoDelete'>
              Since this organization contains other{' '}
              <Link className='link' to={`${ORG_ROUTE}/${org.id}/${ORG_MEMBER_ROUTE}`}>
                members
              </Link>
              , you can&apos;t delete it. Only transfer were allowed.
            </Trans>
          </Info>
        )}
        {!withDelete && filteredMembers.length < 1 ? (
          <Info>
            <Trans i18nKey='transferNotice'>
              You can&apos;t transfer this organization because no eligible{' '}
              <Link className='link' to={`${ORG_ROUTE}/${org.id}/${ORG_MEMBER_ROUTE}`}>
                members
              </Link>{' '}
              can take ownership. Add at least one member who isn&apos;t an owner or yourself to proceed.
            </Trans>
          </Info>
        ) : (
          step === 1 && (
            <Field>
              <Label>{t('New Owner')}</Label>
              <SelectDropdown
                id={props.id ? `${props.id}-member-dd` : undefined}
                withSearch={true}
                onChange={(target) => {
                  if (typeof target === 'object') {
                    setTransferTo(target)
                  } else {
                    setTransferTo(undefined)
                  }
                }}
                searchFilter={(opt, keyword) => {
                  if (typeof opt === 'string') {
                    return true
                  }

                  return !!(opt.email.includes(keyword) || opt.name?.includes(keyword))
                }}
                optionLabel={(opt) => {
                  if (typeof opt === 'object') {
                    return opt.email
                  }
                  return ''
                }}
                renderOption={(opt) => {
                  if (typeof opt === 'object') {
                    return <AvatarCell size={AvatarSize.XS} {...opt} />
                  }
                  return <div>{t('Select member')}</div>
                }}
                value={transferTo}
                options={org ? ['none', ...filteredMembers] : ['none']}
              />
            </Field>
          )
        )}
        {step === 2 && (
          <div>
            <div>{t('Do you really want to transfer ownership?')}</div>
            {org && transferTo && (
              <div className='flex-cc p-4 border bdr my-4'>
                <AvatarCell {...org} />
                <ArrowRight16 width={24} height={24} />
                <AvatarCell {...transferTo} />
              </div>
            )}
            <div>{t('The ownership rights you currently have will not be available once they accept.')}</div>
          </div>
        )}
      </form>
    </ModalGrid>
  )
}

interface ListOrgCardProps {
  /**
   * unique id
   */
  id?: string
  /**
   * list org orgs
   */
  orgs: OrgCardAdapter[]
  /**
   * class name
   */
  className?: string
  /**
   * action to run after update
   */
  onAfterUpdate?: () => void
}

/**
 * list org in card view
 */
export function ListOrgCard(props: ListOrgCardProps) {
  const { t } = useTranslation()
  const { orgs, setOrgs } = useOrgsContext()
  const { onAfterUpdate } = props

  const { imageCache } = useCacheContext()

  const handleLeaveOrg = useHandleLeaveOrg()
  // show delete org modal if not undefined
  const [deleteOrg, setDeleteOrg] = useState<OrgCardAdapter | undefined>(undefined)

  // show transfer org modal if not undefined
  const [transferOrg, setTransferOrg] = useState<OrgCardAdapter | undefined>(undefined)

  // show leave org modal if not undefined
  const [leaveOrg, setLeaveOrg] = useState<OrgCardAdapter | undefined>(undefined)

  // show edit org modal if not undefined
  const [editOrg, setEditOrg] = useState<OrgCardAdapter | undefined>(undefined)

  // is sending update request
  const [isEditLoading, setIsEditLoading] = useBoolean(false)

  const handleHideEdit = () => {
    setEditOrg(undefined)
  }

  const handleShowEdit = (org: OrgCardAdapter) => {
    setEditOrg(org)
  }

  const printAPIError = useReqError()

  const handleSubmitEdit = useCallback(
    (nextName: string, avatar?: ImgEasyResult, done?: () => void) => {
      if (editOrg) {
        const { id, name } = editOrg
        const nextAvatar = avatar && avatar?.src ? avatar.src : ''
        const currentAvatar = editOrg.avatar
        const promises = []
        const isRemoveAvatar = currentAvatar !== '' && nextAvatar === ''

        if (nextName === name && nextAvatar === currentAvatar) {
          return
        }

        if (name !== nextName) {
          promises.push(OrganizationsAPI.ReqUpdateName(id, nextName))
        }

        if (isRemoveAvatar) {
          promises.push(OrganizationsAPI.ReqDeleteAvatar(id))
        }

        if (avatar?.file !== undefined) {
          promises.push(OrganizationsAPI.ReqUpdateAvatar(id, avatar?.file))
        }
        if (promises.length > 0) {
          setIsEditLoading(true)
          Promise.all(promises)
            .then(() => {
              const newOrgs = [...orgs]
              const idx = newOrgs.findIndex((org) => org.id === id)
              if (idx !== -1) {
                if (avatar?.file !== undefined) {
                  const oldAvatar = newOrgs[idx].avatar
                  if (oldAvatar?.startsWith('blob:')) {
                    URL.revokeObjectURL(oldAvatar)
                  }
                  newOrgs[idx].avatar = avatar.src

                  // update image cache
                  imageCache.update(`org-${id}`, Date.now())
                }

                if (isRemoveAvatar) {
                  newOrgs[idx].avatar = undefined
                }

                setOrgs(newOrgs)
              }
              toasted(t('Organization updated!'))
            })
            .catch(printAPIError)
            .finally(() => {
              handleHideEdit()
              setIsEditLoading(false)
              done?.()
              props.onAfterUpdate?.()
            })
        }
      }
    },
    [editOrg, setIsEditLoading, printAPIError, orgs, t, setOrgs, imageCache, props]
  )

  const handleHideLeave = () => {
    setLeaveOrg(undefined)
  }

  const handleSubmitLeave = useCallback(() => {
    if (leaveOrg) {
      handleLeaveOrg({
        leaveOrg: leaveOrg.id,
        onAfterUpdate: onAfterUpdate,
      })
    }
  }, [onAfterUpdate, handleLeaveOrg, leaveOrg])

  const { setTransferTarget, handleHideTransfer, transferTarget, handleTransfer } = useOrgTransferOwnership(
    transferOrg,
    useCallback(() => {
      setTransferOrg(undefined)
    }, [setTransferOrg])
  )

  const { rmTransferTarget, setRmTransferTarget, handleRm, handleRmHide } = useOrgRemove(
    deleteOrg,
    useCallback(
      (isSubmit: boolean, isTransfer: boolean) => {
        setDeleteOrg(undefined)
        if (!isTransfer && isSubmit) {
          onAfterUpdate?.()
        }
      },
      [setDeleteOrg, onAfterUpdate]
    )
  )

  return (
    <div className={clsx('grids-3 gap-8', props.className)}>
      {props.orgs.length > 0 ? (
        <>
          {props.orgs.map((org) => {
            return (
              <OrgCard
                key={org.id}
                org={org}
                onDeleteClick={setDeleteOrg}
                onEditClick={handleShowEdit}
                onTransferClick={setTransferOrg}
                onLeaveClick={setLeaveOrg}
              />
            )
          })}
          {leaveOrg && (
            <DeleteModal
              id='my-orgs-leave-modal'
              show={true}
              onHide={handleHideLeave}
              onSubmit={handleSubmitLeave}
              continueLabel={t('Yes, leave')}
              title={t('Leave Organization')}
              withCancel
            >
              <div className='modal-sm'>
                <div className='text-base mb-2'>{t("Are you sure you'd like to leave this organization?")}</div>
                <div className='text-light'>
                  {t(
                    "You will lose access to all organization assets. To gain back access, you'd have to be invited back."
                  )}
                </div>
              </div>
            </DeleteModal>
          )}
          {editOrg && (
            <BasicInfoEditModal
              id='my-orgs-edit-modal'
              show={true}
              isLoading={isEditLoading}
              onHide={handleHideEdit}
              name={editOrg.name}
              avatar={editOrg.avatar}
              onSubmit={handleSubmitEdit}
              title={t('Edit Organization')}
            />
          )}
          {transferOrg && (
            <OrgTransferModal
              id='my-orgs-trf-modal'
              show={true}
              onHide={handleHideTransfer}
              org={transferOrg}
              transferTo={transferTarget}
              setTransferTo={setTransferTarget}
              onSubmit={(done) => {
                handleTransfer().finally(done)
              }}
            />
          )}
          {deleteOrg && (
            <OrgDeleteModal
              id='my-orgs-delete-modal'
              show={true}
              onHide={handleRmHide}
              org={deleteOrg}
              transferTo={rmTransferTarget}
              setTransferTo={setRmTransferTarget}
              onSubmit={(done) => {
                handleRm().finally(done)
              }}
            />
          )}
        </>
      ) : (
        <div className='text-light'>{t('No record found')}</div>
      )}
    </div>
  )
}
